// @flow

// Parses the underscore-delimited, fully translated carline paths delivered by the
// product-catalog backend (field `fittingCarPaths`, see ticket #53422) into a generic
// tree of arbitrary depth. The hierarchy has NO fixed semantic per level: depending on
// the brand, the model year may sit on level 2, 3 or 4 — or be absent. We therefore
// detect "year levels" per node via a heuristic instead of assuming a fixed depth.

export const FITMENT_DELIMITER = "_";

const YEAR_PATTERN = /^\d{4}$/;
const YEAR_MIN = 1950;
const YEAR_MAX = 2100;
// 20 leaf entries (level-3 tuples) ~ 7 rows in the current 3-column layout — the point
// where the module starts to scroll. Above it we collapse to level 2 by default.
export const AUTO_EXPAND_LEAF_LIMIT = 20;

export type FitmentNode = {
    label: string,
    depth: number,
    children: FitmentNode[],
    isYearLevel: boolean,
    years: number[],          // pure 4-digit years directly under this node
    literalYears: string[],   // year-like but non-numeric labels (e.g. "ab 2020")
};

// A pure, in-range 4-digit year ("2019").
export const isYear = (label: string): boolean => {
    if (!YEAR_PATTERN.test(label)) return false;
    const n = parseInt(label, 10);
    return n >= YEAR_MIN && n <= YEAR_MAX;
};

// Year-like: a pure year OR a label that contains a plausible 4-digit year ("ab 2020").
// "Variant A" / "XC90 II" are NOT year-like, so a node mixing them with years is treated
// as a regular hierarchy node (see ticket: mixed children must not be a year level).
export const isYearLike = (label: string): boolean => {
    if (isYear(label)) return true;
    const match = label.match(/(?:^|\D)(\d{4})(?:\D|$)/);
    if (!match) return false;
    const n = parseInt(match[1], 10);
    return n >= YEAR_MIN && n <= YEAR_MAX;
};

// Compresses a list of years into human-readable ranges:
//   [2018,2019,2020,2022,2023] -> ["2018–2020", "2022–2023"]
export const compressYears = (years: number[]): string[] => {
    const sorted = [...new Set(years)].sort((a, b) => a - b);
    const ranges = [];
    let start = null;
    let prev = null;
    sorted.forEach(year => {
        if (start === null) {
            start = year;
            prev = year;
        } else if (year === prev + 1) {
            prev = year;
        } else {
            ranges.push(start === prev ? `${start}` : `${start}–${prev}`);
            start = year;
            prev = year;
        }
    });
    if (start !== null) {
        ranges.push(start === prev ? `${start}` : `${start}–${prev}`);
    }
    return ranges;
};

// Detects whether the direct children of a node form a year level and, if so, folds them
// into year chips on the node itself. A year level requires: at least one child, every
// child is year-like, at least one child is a pure year, and no year child has its own
// children. Otherwise the node keeps its children as regular sub-nodes (recursively).
const foldYearLevel = (node: FitmentNode): FitmentNode => {
    node.children.forEach(foldYearLevel);

    const hasChildren = node.children.length > 0;
    const allYearLike = hasChildren && node.children.every(c => isYearLike(c.label));
    const anyPureYear = node.children.some(c => isYear(c.label));
    const noGrandchildren = node.children.every(c => c.children.length === 0);

    if (hasChildren && allYearLike && anyPureYear && noGrandchildren) {
        node.isYearLevel = true;
        node.years = node.children
            .filter(c => isYear(c.label))
            .map(c => parseInt(c.label, 10));
        node.literalYears = node.children
            .filter(c => !isYear(c.label))
            .map(c => c.label);
        node.children = [];
    }
    return node;
};

const sortTree = (nodes: FitmentNode[]): FitmentNode[] => {
    nodes.sort((a, b) => a.label.localeCompare(b.label));
    nodes.forEach(n => sortTree(n.children));
    return nodes;
};

// Splits a single path into its segments, dropping the leading empty token (paths start
// with the delimiter) and the leading brand segment (we always render within one brand).
// Segments below the first pure-year segment (e.g. engine codes such as "EH"/"EP") are
// out of scope per ticket #53422 and cut off — the year stays a leaf, so year levels can
// fold into range chips instead of rendering the codes as an extra drill-down level.
const splitPath = (path: string): string[] => {
    const segments = path
        .split(FITMENT_DELIMITER)
        .filter(segment => segment.length > 0)
        .slice(1);
    const yearIndex = segments.findIndex(isYear);
    return yearIndex === -1 ? segments : segments.slice(0, yearIndex + 1);
};

/**
 * Builds the fitment tree from the raw `fittingCarPaths`. Handles mixed path depths,
 * deduplication and unsorted input. Returns the top-level nodes (brand already stripped).
 */
export const buildFitmentTree = (paths: ?(string[]), brandName?: string): FitmentNode[] => {
    if (!paths || paths.length === 0) return [];

    const roots: FitmentNode[] = [];
    const indexOf = (siblings: FitmentNode[], label: string, depth: number): FitmentNode => {
        let existing = siblings.find(s => s.label === label);
        if (!existing) {
            existing = {label, depth, children: [], isYearLevel: false, years: [], literalYears: []};
            siblings.push(existing);
        }
        return existing;
    };

    paths.forEach(path => {
        const segments = splitPath(path);
        if (segments.length === 0) return;
        let level = roots;
        segments.forEach((segment, depth) => {
            const node = indexOf(level, segment, depth);
            level = node.children;
        });
    });

    roots.forEach(foldYearLevel);
    return sortTree(roots);
};

// True when a node represents "fits all model years": a leaf below the top series level
// with no detected year information. The backend ends a path at the model level when the
// product fits the model irrespective of year, which is exactly this signal.
export const fitsAllModelYears = (node: FitmentNode): boolean =>
    node.depth >= 1 &&
    node.children.length === 0 &&
    node.years.length === 0 &&
    node.literalYears.length === 0;

// Number of leaf entries (level-3 tuples) in the subtree — drives the auto-expand limit.
export const countLeaves = (nodes: FitmentNode[]): number =>
    nodes.reduce((sum, node) => {
        if (node.isYearLevel) return sum + node.years.length + node.literalYears.length;
        if (node.children.length === 0) return sum + 1;
        return sum + countLeaves(node.children);
    }, 0);
